import type { StateCreator } from 'zustand';
import type { IssueGridParameters } from './types';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import { DEFAULT_FILTER_MODEL } from '../../models/filterModel/constants';
import {
  getIssueFilterModelApiSignature,
  getIssueFilterModelUiSignature,
} from '../../models/filterModel/filterModel';
import { DEFAULT_PARAMETERS } from './constants';
import { IssueParametersSlice } from './types';

const getInitialParameters = (projectId: string | undefined): IssueGridParameters => {
  if (!projectId) {
    return DEFAULT_PARAMETERS;
  }

  return LocalStorageManager.getIssueParameters(projectId, DEFAULT_PARAMETERS);
};

const DEFAULT_FILTER_MODEL_UI_SIGNATURE =
  getIssueFilterModelUiSignature(DEFAULT_FILTER_MODEL);

const DEFAULT_FILTER_MODEL_API_SIGNATURE =
  getIssueFilterModelApiSignature(DEFAULT_FILTER_MODEL);

type IssueParametersSliceCreator<T> = StateCreator<
  T,
  [],
  [],
  IssueParametersSlice
>;

export const createIssueParametersSlice: IssueParametersSliceCreator<any> = (
  set,
  get
) => ({
  parameters: DEFAULT_PARAMETERS,
  projectId: undefined,
  filterModel: DEFAULT_FILTER_MODEL,
  filterModelUiSignature: DEFAULT_FILTER_MODEL_UI_SIGNATURE,
  filterModelApiSignature: DEFAULT_FILTER_MODEL_API_SIGNATURE,
  setParameters: (data) => {
    const currentParameters = get().parameters;
    const projectId = get().projectId;
    const newParameters = {
      ...currentParameters,
      ...data,
    };

    set({ parameters: newParameters });

    if (projectId) {
      LocalStorageManager.saveIssueParameters(projectId, newParameters);
    }
  },
  setFilterModel: (filterModel) => {
    set({
      filterModel,
      filterModelUiSignature: getIssueFilterModelUiSignature(filterModel),
      filterModelApiSignature: getIssueFilterModelApiSignature(filterModel),
    });

    const projectId = get().projectId;
    if (projectId) {
      LocalStorageManager.saveIssueParameters(projectId, get().parameters);
    }
  },
  resetParameters: () => {
    set({
      parameters: DEFAULT_PARAMETERS,
      filterModel: DEFAULT_FILTER_MODEL,
      filterModelUiSignature: DEFAULT_FILTER_MODEL_UI_SIGNATURE,
      filterModelApiSignature: DEFAULT_FILTER_MODEL_API_SIGNATURE,
    });

    const projectId = get().projectId;
    if (projectId) {
      LocalStorageManager.saveIssueParameters(projectId, DEFAULT_PARAMETERS);
    }
  },
  setProjectId: (projectId) => {
    const initialParameters = getInitialParameters(projectId);
    set({
      projectId,
      parameters: initialParameters,
    });
  },
});

export type { IssueParametersSlice } from './types';
